/* AvoClima Internationalization — English + Swahili */

const en = {
  nav: {
    home: 'Home',
    dashboard: 'Dashboard',
    about: 'About',
    myTrees: 'My Trees',
    ndvi: 'NDVI',
    eudr: 'EUDR',
    crcf: 'Carbon Farming',
    labels: 'Labels',
    profile: 'Profile',
    admin: 'Admin',
    login: 'Log In',
    register: 'Register',
    logout: 'Log Out',
  },
  tree: {
    register: 'Register Tree',
    edit: 'Edit Tree',
    remove: 'Remove',
    species: 'Species',
    dbh: 'DBH at breast height (cm)',
    plantingDate: 'Planting Date',
    photo: 'Photo',
    frontPhoto: 'Front Photo (required for height & crown estimation)',
    gps: 'Tree Location',
    captureGps: 'Capture My Location',
    reCaptureGps: 'Re-capture Location',
    fixGps: 'Fixing GPS...',
    standAtTree: 'Stand at the tree and capture GPS',
    gpsNotAvailable: 'GPS not available on this device.',
    locationLocked: '📍 Location locked ({accuracy}m precision)',
    gpsFixing: '📍 GPS fixing... ({accuracy}m)',
    permissionDenied: 'Location permission denied.',
    gpsUnavailable: 'GPS unavailable.',
    gpsTimedOut: 'GPS timed out.',
    gpsError: 'GPS error.',
    waitGps: '📍 Wait for GPS...',
    gpsRequired: 'GPS location and DBH are required.',
    notes: 'Field Notes',
    notesPlaceholder: 'e.g. termite damage, recently pruned, multi-stem...',
    dbhPlaceholder: 'e.g. 15.2',
    heightPlaceholder: 'e.g. 8.5',
    co2: 'kg CO₂',
    addPhoto: 'Add {angle} photo',
    stepIndicator: 'Step {current} of {total}',
    step1Label: 'Location & DBH',
    step2Label: 'Species & Details',
    step3Label: 'Photos & Date',
    openCamera: 'Tap to open camera',
    selectFromGallery: 'or select from gallery',
    removePhoto: 'Remove photo',
    photoInstructionsTitle: '📸 How to take the photo:',
    photoInstr1: '📏 Plant three 3m rods in a triangle around the tree trunk',
    photoInstr2: '🟢 Stand outside the rod triangle, far enough to capture the full tree',
    photoInstr3: '🟢 Hold phone vertically (portrait)',
    photoInstr4: '🟢 Capture the entire tree — the rods should be visible in the frame',
    photoInstr5: '🟢 One tree per photo, tree should fill most of the frame',
    photoInstr6: "🟢 Don't move the rods — shoot all 4 angles from outside the triangle",
    photoInstr7: '🟢 Tip: If you only have 1 rod, plant it next to the trunk — accuracy will be lower',
    photoInstr8: '✅ Good photo = accurate height & crown estimation',
    loadFailed: 'Failed to load tree',
    saveFailed: 'Failed to save tree',
    queuedSync: '📦 Queued for sync when online',
    saved: '✅ Saved! {co2} kg CO₂{photoMsg}',
    photoCaptured: '📸 {angle} photo captured!',
    photoNoRods: '⚠️ No rods detected — check rod placement',
    photoFewRods: '⚠️ {count}/3 rods — stand further back',
    photoGoodRods: '✅ 3 rods, RAI {rai}%',
    uploadingPhoto: '⏳ Uploading...',
    treePhotos: 'Tree Photos ({count})',
    takeFourPhotos: 'Take 4 photos (front, left, right, back) for complete 3D tree measurement',
    pgQuality: '📊 Photogrammetry Quality',
    avgRods: 'Avg rods/photo: {avg} · {valid}/{total} angles with height',
    convergenceExcellent: '✅ Heights converge (within 5%)',
    convergenceGood: '✅ Heights consistent (within 10%)',
    convergenceFair: '⚠️ Heights vary by {pct}% — retake poor angles',
    convergencePoor: '❌ Heights inconsistent — rods may have moved',
    pgHintCaptureMore: '📸 Capture remaining angles for complete coverage',
    pgHintRetakeRods: '🔄 Retake photos with only rod visible — step further back',
    pgHintMoreAngles: '📸 2 more angles needed for convergence check',
    pgHintConfirm: '📸 One more angle to confirm consistency',
    pgSufficient: '✅ Sufficient quality — proceed',
    doneViewTrees: 'Done — View My Trees',
    noTrees: 'No trees yet',
    firstTree: 'Stand under your first tree, tap "Add Tree", and capture its GPS location.',
  },
  farm: {
    name: 'Farm Name',
    location: 'Location',
    hectares: 'Total Hectares',
    phone: 'Phone',
    boundary: 'Farm Boundary',
    idDoc: 'Government ID',
    landDeed: 'Land Deed / Title',
    upload: 'Upload',
    uploaded: 'Uploaded',
    pendingVerification: 'Pending verification',
    verified: 'Verified',
    missing: 'Missing',
  },
  eudr: {
    title: 'EUDR Compliance',
    compliant: 'EUDR Compliant',
    attention: 'Attention Required',
    checklist: 'Compliance Checklist',
    deforestationFree: 'Deforestation-free',
    legality: 'Legality',
    geolocation: 'Geolocation',
    traceability: 'Traceability',
    export: 'Due Diligence Export',
    loading: 'Loading compliance data...',
    loadError: 'Failed to load compliance data',
    loadErrorTitle: 'Failed to Load EUDR Data',
    exportError: 'Export failed — try again',
    generating: 'Generating...',
    readyForEU: 'All requirements met — your farm is ready for EU market access',
    attentionDetail: 'Complete the items below to meet EU Deforestation Regulation (2023/1115) requirements',
    treesRegistered: 'Trees Registered',
    deforestationRisk: 'Deforestation Risk',
    idDocument: 'ID Document',
    none: 'None',
    article3a: 'EUDR Article 3(a) — Deforestation-free',
    article3aDetail: 'All {total} trees have NDVI ≥ 0.3 in available Sentinel-2 records (EUDR cutoff: Dec 2020)',
    article3aNoTrees: 'Register at least one tree to begin monitoring',
    article3aFlagged: '{count} tree{s} flagged — review NDVI monitoring page',
    article3bIdentity: 'EUDR Article 3(b) — Legality (Farmer Identity)',
    article3bIdVerified: 'Government ID uploaded and verified by admin',
    article3bIdPending: 'ID uploaded, pending admin verification',
    article3bIdMissing: 'Upload government ID in Profile',
    article3bLand: 'EUDR Article 3(b) — Legality (Land Tenure)',
    article3bLandVerified: 'Land deed uploaded and verified by admin',
    article3bLandPending: 'Land deed uploaded, pending admin verification',
    article3bLandMissing: 'Upload land deed in Profile',
    article9Geo: 'EUDR Article 9(1)(d) — Geolocation',
    article9GeoDetail: '{count} trees with GPS coordinates registered',
    article9GeoNoTrees: 'No trees registered — add your first tree',
    article9Trace: 'EUDR Article 9(1)(a) — Traceability',
    article9TraceDetail: 'All data recorded on immutable audit trail (SHA256 hash-chain)',
    view: 'View',
    fix: 'Fix',
    dueDiligenceStatement: 'Due Diligence Statement',
    downloadJSON: 'Download JSON',
    statementId: 'Statement ID:',
  },
  ndvi: {
    title: 'NDVI Monitoring',
    loading: 'Loading NDVI data...',
    loadError: 'Failed to load NDVI data',
    loadErrorTitle: 'Failed to Load NDVI',
    noTrees: 'No Trees Yet',
    noTreesDetail: 'Register trees to see NDVI monitoring data.',
    treesMonitored: 'Trees Monitored',
    highRisk: 'High Risk',
    mediumRisk: 'Medium Risk',
    lowRisk: 'Low Risk',
    flagged: '{count} tree{s} flagged for deforestation risk. Review the NDVI history below.',
    ndviDataPoints: 'NDVI data points: {count} | Latest: {value}',
    noNDVI: 'no NDVI',
    na: 'N/A',
    refresh: 'Refresh',
  },
  qrLabels: {
    title: 'Tree Labels',
    loading: 'Loading labels...',
    loadError: 'Failed to load tree labels',
    loadErrorTitle: 'Failed to Load Labels',
    noTrees: 'No Trees',
    noTreesDetail: 'Register trees to print QR code labels.',
    instructions: 'Label Instructions:',
    instruction1: 'Print on sticker paper (A4, 2" × 1" labels recommended)',
    instruction2: 'Cut labels and attach to trees with aluminum tag + wire',
    instruction3: 'QR code links to public tree page with full carbon data',
    instruction4: 'Labels are waterproof if laminated or using polyester sticker paper',
    printLabels: 'Print {count} Labels',
    co2: 'kg CO₂',
    printTitle: 'AvoClima Tree Labels — {farmer}',
    treeLabels: '{count} tree labels',
  },
  crcf: {
    title: 'EU CRCF Carbon Farming',
    baseline: 'Carbon Baseline',
    additionality: 'Additionality',
    monitoring: 'Monitoring Plan',
    tco2: 'tCO₂ Total',
    tco2PerHa: 'tCO₂/ha',
    soc: 'SOC (tC/ha est.)',
    species: 'Species',
    additional: 'Additional Carbon',
    notAdditional: 'Not Additional',
  },
  common: {
    loading: 'Loading...',
    save: 'Save Changes',
    saving: 'Saving...',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    back: 'Back',
    next: 'Next',
    download: 'Download',
    print: 'Print',
    refresh: 'Refresh',
    offline: 'You are offline — data will sync automatically',
    pendingSync: '{count} pending sync item(s)',
    syncNow: 'Sync Now',
    retry: 'Retry',
  },
  home: {
    tagline: 'Geospatial Tree Inventory & Monitoring for Agroforestry Carbon Markets',
    mission: 'Our Mission',
    missionText: 'AvoClima empowers smallholder farmers with high-integrity tree inventory data, satellite monitoring, and carbon credit certification — unlocking climate finance for rural communities.',
    howItWorks: 'How It Works',
    step1Title: 'Register Your Farm',
    step1Desc: 'Create your farm profile, upload ID documents and land deed, and map your boundary using GPS.',
    step2Title: 'Add Trees with Photos',
    step2Desc: 'Stand under each tree, measure its DBH with a diameter tape, capture GPS location and 4-angle photos. Our photogrammetry engine computes height and crown from your photos. Carbon is calculated from your DBH measurement and species selection.',
    step3Title: 'Satellite Monitoring',
    step3Desc: 'Monthly Sentinel-2 NDVI tracks vegetation health and detects deforestation risk, providing ongoing satellite verification of tree survival.',
    step4Title: 'Carbon Certification',
    step4Desc: 'Meet EUDR compliance and CRCF carbon farming requirements. Generate due diligence statements and carbon credit reports.',
    carbonEngine: 'The Carbon Engine',
    aboveGroundBiomass: 'Above Ground Biomass (kg)',
    carbonContent: 'Carbon content (IPCC 0.47 default)',
    co2Equivalent: 'CO₂ equivalent (×3.67)',
    monteCarloNote: 'Carbon calculations use Monte Carlo uncertainty analysis (10,000 iterations) with 95% confidence bounds.',
  },
  dashboard: {
    loadError: 'Failed to load map data',
  },
  admin: {
    title: 'Admin Dashboard',
    accessDenied: 'Access Denied',
    privilegesRequired: 'Admin privileges required.',
    loading: 'Loading admin panel...',
    loadError: 'Failed to load admin data',
    overview: 'Overview',
    farmers: 'Farmers',
    allTrees: 'All Trees',
    ndviStatus: 'NDVI Status',
    charts: 'Charts',
    eudrExport: 'EUDR Export',
    pendingCreditsTab: 'Pending Credits ({count})',
    totalFarmers: 'Total Farmers',
    totalTrees: 'Total Trees',
    activeTrees: 'Active Trees',
    co2Issued: 'CO2 Issued (kg)',
    pending: 'Pending',
    approvedCredits: 'Approved Credits',
    issuedCredits: 'Issued Credits',
    monitoringTrends: 'Monitoring Trends',
    monitoringDesc: 'Monthly trends, species and health distribution, NDVI breakdown, and carbon credit status.',
    tableId: 'ID',
    tableName: 'Name',
    tableEmail: 'Email',
    tableFarm: 'Farm',
    tableTrees: 'Trees',
    tableVerified: 'Verified',
    tableDocs: 'Docs',
    tableActions: 'Actions',
    verified: 'Verified',
    pending: 'Pending',
    none: 'None',
    admin: 'Admin',
    unverify: 'Unverify',
    verify: 'Verify',
    verifyDocs: 'Verify Docs',
    delete: 'Delete',
    tableSpecies: 'Species',
    tableDbh: 'DBH',
    tableCo2: 'CO2 (kg)',
    tableStatus: 'Status',
    ndviDesc: 'NDVI satellite vegetation status for all active trees. Red = high deforestation risk.',
    tableTreeId: 'Tree ID',
    ndvi: 'NDVI',
    date: 'Date',
    risk: 'Risk',
    noNDVIData: 'No NDVI data available.',
    low: 'LOW',
    medium: 'MEDIUM',
    high: 'HIGH',
    riskInfo: 'High risk: NDVI < 0.1 | Medium: 0.1-0.3 | Low: > 0.3',
    eudrTitle: 'EUDR Due Diligence Export',
    eudrDesc: 'Generate EUDR due diligence statements for farmers. This exports GPS coordinates, NDVI time series, farm boundary, deforestation risk, and farmer verification status in a format compatible with the EU Information System requirements (Regulation 2023/1115).',
    ndviAvg: 'NDVI Avg',
    generating: 'Generating...',
    dueDiligence: 'Due Diligence',
    eudrStatement: 'EUDR Statement',
    downloadJSON: 'Download JSON',
    noPendingCredits: 'No pending credits to review.',
    approve: 'Approve',
    approveAndIssue: 'Approve & Issue',
    howCreditsWorkTitle: 'How Credit Issuance Works',
    creditStep1: 'Tree measurements create pending carbon ledger entries',
    creditStep2: 'Admin approves the calculation (validates DBH, species, data integrity)',
    creditStep3: 'Admin issues the credit, generating a verifiable AC-XXXXXXX credit ID',
    creditStep4: 'Anyone can verify a credit at /api/v1/carbon/verify with the credit ID',
    deleteFarmerTitle: 'Delete Farmer',
    deleteFarmerMsg: 'Delete farmer "{name}" and ALL their trees, photos, and data? This cannot be undone.',
    deleteTreeTitle: 'Delete Tree',
    deleteTreeMsg: 'Delete tree #{id} and all its data?',
    creditIssued: 'Credit Issued!',
    creditId: 'Credit ID: {id}',
    exportFailed: 'Export Failed',
    exportFailedMsg: 'Could not generate EUDR statement',
  },
  profile: {
    title: 'My Profile',
    email: 'Email',
    name: 'Name',
    emailCannotBeChanged: 'Email cannot be changed',
    namePlaceholder: 'Your name',
    farmNamePlaceholder: 'Your farm name',
    locationPlaceholder: 'e.g. Kiambu, Kenya',
    hectaresPlaceholder: '0.0',
    phonePlaceholder: '+254...',
    eudrDocs: 'EUDR Compliance Documents',
    notUploaded: 'Not uploaded',
    uploadedVerified: '(Verified)',
    uploadedPending: '(Pending verification)',
    saveSuccess: 'Profile updated successfully',
    saveFailed: 'Failed to save profile',
    idUploaded: 'ID uploaded',
    landDeedUploaded: 'Land deed uploaded',
    uploadFailed: 'Upload failed for {type}',
    docLoadError: 'Could not load document info',
    uploading: 'Uploading...',
  },
  auth: {
    login: 'Log In',
    loggingIn: 'Logging in...',
    email: 'Email',
    emailPlaceholder: 'your@email.com',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    forgotPassword: 'Forgot password?',
    noAccount: "Don't have an account?",
    registerHere: 'Register here',
    loginFailed: 'Login failed',
    register: 'Create Your Account',
    registerDesc: 'Register your farm to start tracking trees and carbon storage.',
    fullName: 'Full Name',
    fullNamePlaceholder: 'e.g. Jane Wanjiku',
    passwordPlaceholderRegister: 'At least 8 characters',
    farmNamePlaceholder: 'e.g. Sunrise Farm',
    locationPlaceholder: "e.g. Murang'a, Kenya",
    hectaresPlaceholder: 'e.g. 3.8',
    creating: 'Creating account...',
    create: 'Create Account',
    alreadyAccount: 'Already have an account?',
    loginLink: 'Log in',
    allFieldsRequired: 'All fields are required.',
    passwordMinLength: 'Password must be at least 8 characters.',
    registrationFailed: 'Registration failed',
    welcome: 'Welcome, {name}! Check your email (or server console) to verify your account.',
    resetPassword: 'Reset Password',
    sendResetLink: 'Send Reset Link',
    sending: 'Sending...',
    resetSent: 'If that email is registered, a reset link has been sent.',
    resetDevNote: '(In development mode, check the server console for the reset token.)',
    backToLogin: 'Back to Login',
    enterEmail: "Enter your email and we'll send you a reset link.",
    resetFail: 'Reset request failed',
    setNewPassword: 'Set New Password',
    newPassword: 'New Password',
    newPasswordPlaceholder: 'At least 8 characters',
    resetting: 'Resetting...',
    resetPwdBtn: 'Reset Password',
    invalidResetLink: 'Invalid Reset Link',
    noResetToken: 'No reset token found. Request a new reset link.',
    requestReset: 'Request Reset',
    resetSuccess: 'Password reset successful!',
    resetFailed: 'Reset failed. The link may have expired or been used already.',
  },
  field: {
    title: 'Field Validation',
    pending: '{count} pending',
    syncing: 'Syncing...',
    syncAll: 'Sync All ({count})',
    measurementsSynced: 'Measurements synced to server',
    selectTree: 'Select Tree',
    noTreesFound: 'No trees found',
    treeNum: 'Tree #{id} Measurements',
    selectATree: 'Select a tree',
    dbh: 'DBH (cm)',
    gpsCoords: 'GPS Coordinates',
    latitude: 'Latitude',
    longitude: 'Longitude',
    fixingGps: 'Fixing GPS...',
    captureGps: 'Capture GPS',
    recaptureGps: 'Re-capture GPS',
    fieldNotes: 'Field Notes',
    notesPlaceholder: 'Notes on tree condition, pests, etc.',
    clear: 'Clear',
    saveAll: 'Save All',
    offlineSafe: 'Offline-Safe Data Collection',
    offlineNote1: 'All measurements are saved locally in your browser and survive page refreshes',
    offlineNote2: 'When you click Sync All, data is sent to the server one tree at a time',
    offlineNote3: 'If the server is unreachable, data stays in local storage until the next sync attempt',
    offlineNote4: 'Use the CSV Export button to download all measurements as a spreadsheet for backup',
    offlineNote5: 'GPS capture uses the browser geolocation API (enable high accuracy for ±3 m precision)',
    csv: 'CSV',
    treeLoadError: 'Could not load trees from server — check connection',
  },
  trends: {
    monthlyTrees: 'Monthly Tree Registrations',
    monthlyCarbon: 'Monthly CO₂ (kg)',
    monthlyFarmers: 'Monthly Farmer Registrations',
    speciesDistribution: 'Species Distribution',
    healthStatus: 'Health Status',
    ndviDistribution: 'NDVI Distribution',
    creditStatus: 'Credit Status',
    noData: 'No trend data',
    max: 'max',
  },
  crcf: {
    title: 'EU CRCF Carbon Farming',
    baseline: 'Carbon Baseline',
    additionality: 'Additionality',
    monitoring: 'Monitoring Plan',
    tco2: 'tCO₂ Total',
    tco2PerHa: 'tCO₂/ha',
    soc: 'SOC (tC/ha est.)',
    species: 'Species',
    additional: 'Additional Carbon',
    notAdditional: 'Not Additional',
    loading: 'Loading carbon farming data...',
    loadError: 'Failed to load carbon farming data',
    noTreesYet: 'No Trees Yet',
    noTreesDesc: 'Register trees to establish your carbon baseline.',
    retry: 'Retry',
    regulation: 'Regulation (EU) 2024/3012 — Carbon Removal Certification Framework',
    whatThisMeans: 'What this means:',
    crcfBaselineDesc: 'This is your CRCF baseline. All future carbon gains are measured against this starting point. SOC uses IPCC Tier 1 default values based on climate zone and soil type — a professional soil test can refine this value. The baseline must be established before carbon farming activities begin.',
    carbonAdditionality: 'Carbon Additionality',
    bauReference: 'BAU reference ({reference})',
    currentTreeCarbon: 'Current tree carbon',
    estimatedSocGain: 'Estimated SOC gain (10yr)',
    additionalCarbon: 'Additional carbon',
    barriersToAddress: 'Barriers to address',
    monitoringPlan: 'Monitoring Plan',
    parameter: 'Parameter',
    method: 'Method',
    frequency: 'Frequency',
    qualityAssurance: 'Quality Assurance',
  },
  protected: {
    signInRequired: 'Sign In Required',
    description: 'You need to be logged in to access this page.',
  },
  about: {
    title: 'About AvoClima',
    problem: 'The Problem',
    problemText: 'Agroforestry is expanding rapidly across East Africa — avocado, mango, macadamia, grevillea, eucalyptus, and coffee farms are sequestering significant carbon. Yet farmers, cooperatives, and exporters lack practical tools to measure, monitor, and verify tree carbon stocks. Existing dMRV platforms are expensive, complex, and not designed for smallholder agroforestry.',
    solution: 'Our Solution',
    solutionText: 'AvoClima is a geospatial tree inventory and monitoring platform built for smallholder agroforestry. It combines:',
    feature1Title: 'MSH-15 field collection',
    feature1Desc: 'Our multi-sensor instrument captures tree biometrics with survey-grade RTK GPS',
    feature2Title: 'Species-specific carbon engine',
    feature2Desc: '16 tree species with Monte Carlo uncertainty bounds',
    feature3Title: 'Multi-camera photogrammetry',
    feature3Desc: 'reconstructs height, DBH and crown from 12 synchronized cameras',
    feature4Title: 'Immutable audit trail',
    feature4Desc: 'every action logged for measurement verifiability',
    feature5Title: 'Satellite cross-check',
    feature5Desc: 'Sentinel-2 NDVI validates vegetation at every tree location',
    techStack: 'Technology Stack',
    verifiability: 'Verifiability',
    verifiabilityText: 'Every tree measurement creates a timestamped carbon ledger entry with quantified uncertainty. Photo EXIF GPS is verified against the tree\'s registered coordinates for location consistency. Vegetation scores from pixel analysis confirm tree presence. The immutable audit log records every action. Sentinel-2 NDVI provides independent satellite verification.',
  },
};

const sw = {
  nav: {
    home: 'Nyumbani',
    dashboard: 'Dashibodi',
    about: 'Kuhusu',
    myTrees: 'Miti Yangu',
    ndvi: 'NDVI',
    eudr: 'EUDR',
    crcf: 'Ukulima wa Kaboni',
    labels: 'Leburu',
    profile: 'Wasifu',
    admin: 'Msimamizi',
    login: 'Ingia',
    register: 'Jisajili',
    logout: 'Toka',
  },
  tree: {
    register: 'Sajili Mti',
    edit: 'Hariri Mti',
    remove: 'Ondoa',
    species: 'Aina ya Mti',
    dbh: 'Kipenyo cha mti kifuani (cm)',
    frontPhoto: 'Picha ya Mbele (inahitajika kwa ukadiriaji wa urefu na kifuniko)',
    plantingDate: 'Tarehe ya Kupanda',
    photo: 'Picha',
    gps: 'Mahali pa Mti',
    captureGps: 'Nasa Mahali Pangu',
    reCaptureGps: 'Nasa Mahali Tena',
    fixGps: 'Inatafuta GPS...',
    standAtTree: 'Simama chini ya mti na nase GPS',
    gpsNotAvailable: 'GPS haipatikani kwenye kifaa hiki.',
    locationLocked: '📍 Mahali pamefungwa (usahihi wa {accuracy}m)',
    gpsFixing: '📍 Inatafuta GPS... ({accuracy}m)',
    permissionDenied: 'Ruhusa ya eneo imekataliwa.',
    gpsUnavailable: 'GPS haipatikani.',
    gpsTimedOut: 'GPS imeisha muda.',
    gpsError: 'Hitilafu ya GPS.',
    waitGps: '📍 Subiri GPS...',
    gpsRequired: 'Mahali pa GPS na DBH vinahitajika.',
    notes: 'Maelezo ya Shamba',
    notesPlaceholder: 'mf. uharibifu wa mchwa, kupogolewa hivi karibuni, shina nyingi...',
    dbhPlaceholder: 'mf. 15.2',
    heightPlaceholder: 'mf. 8.5',
    co2: 'kg CO₂',
    addPhoto: 'Ongeza picha ya {angle}',
    stepIndicator: 'Hatua {current} kati ya {total}',
    step1Label: 'Mahali na DBH',
    step2Label: 'Aina na Maelezo',
    step3Label: 'Picha na Tarehe',
    openCamera: 'Gonga kufungua kamera',
    selectFromGallery: 'au chagua kutoka kwenye nyumba ya picha',
    removePhoto: 'Ondoa picha',
    photoInstructionsTitle: '📸 Jinsi ya kupiga picha:',
    photoInstr1: '📏 Panda fimbo tatu za mita 3 kwenye pembetatu kuzunguka shina la mti',
    photoInstr2: '🟢 Simama nje ya pembetatu ya fimbo, mbali vya kutosha kunasa mti mzima',
    photoInstr3: '🟢 Shikilia simu wima (portrait)',
    photoInstr4: '🟢 Nasa mti mzima — fimbo zionekane kwenye fremu',
    photoInstr5: '🟢 Mti mmoja kwa picha, mti ujaze sehemu kubwa ya fremu',
    photoInstr6: '🟢 Usisogeze fimbo — piga pembe zote 4 kutoka nje ya pembetatu',
    photoInstr7: '🟢 Kidokezo: Ikiwa una fimbo 1 tu, ipande karibu na shina — usahihi utakuwa mdogo',
    photoInstr8: '✅ Picha nzuri = ukadiriaji sahihi ya urefu na kifuniko',
    loadFailed: 'Imeshindwa kupakia mti',
    saveFailed: 'Imeshindwa kuhifadhi mti',
    queuedSync: '📦 Imepangwa kwa ulandanishi mtandaoni',
    saved: '✅ Imehifadhiwa! {co2} kg CO₂{photoMsg}',
    photoCaptured: '📸 Picha ya {angle} imenaswa!',
    photoNoRods: '⚠️ Hakuna fimbo zilizogunduliwa — angalia uwekaji wa fimbo',
    photoFewRods: '⚠️ {count}/3 fimbo — simama nyuma zaidi',
    photoGoodRods: '✅ Fimbo 3, RAI {rai}%',
    uploadingPhoto: '⏳ Inapakia...',
    treePhotos: 'Picha za Mti ({count})',
    takeFourPhotos: 'Piga picha 4 (mbele, kushoto, kulia, nyuma) kwa upimaji kamili wa mti wa 3D',
    pgQuality: '📊 Ubora wa Photogrammetry',
    avgRods: 'Wastani wa fimbo/picha: {avg} · {valid}/{total} pembe zenye urefu',
    convergenceExcellent: '✅ Urefu unaungana (ndani ya 5%)',
    convergenceGood: '✅ Urefu unaendana (ndani ya 10%)',
    convergenceFair: '⚠️ Urefu unatofautiana kwa {pct}% — piga upya pembe mbaya',
    convergencePoor: '❌ Urefu hauendani — fimbo zinaweza kuwa zimehamia',
    pgHintCaptureMore: '📸 Nasa pembe zilizobaki kwa ukamilifu',
    pgHintRetakeRods: '🔄 Piga upya picha kwa fimbo tu — songa nyuma zaidi',
    pgHintMoreAngles: '📸 Pembe 2 zaidi zinahitajika kwa ukaguzi wa muunganiko',
    pgHintConfirm: '📸 Pembe moja zaidi kuthibitisha uthabiti',
    pgSufficient: '✅ Ubora wa kutosha — endelea',
    doneViewTrees: 'Imekamilika — Angalia Miti Yangu',
    noTrees: 'Hakuna miti bado',
    firstTree: 'Simama chini ya mti wako wa kwanza, bofya "Ongeza Mti", na nase eneo la GPS.',
  },
  farm: {
    name: 'Jina la Shamba',
    location: 'Mahali',
    hectares: 'Hekta',
    phone: 'Namba ya Simu',
    boundary: 'Mpaka wa Shamba',
    idDoc: 'Kitambulisho cha Serikali',
    landDeed: 'Hati ya Ardhi',
    upload: 'Pakia',
    uploaded: 'Imepakiwa',
    pendingVerification: 'Inasubiri uthibitisho',
    verified: 'Imethibitishwa',
    missing: 'Haipo',
  },
  eudr: {
    title: 'Uzingatiaji wa EUDR',
    compliant: 'Inazingatia EUDR',
    attention: 'Inahitaji Umakini',
    checklist: 'Orodha ya Uzingatiaji',
    deforestationFree: 'Bila ukataji miti',
    legality: 'Uhalali',
    geolocation: 'Mahali pa kijiografia',
    traceability: 'Ufuatiliaji',
    export: 'Utoaji wa Hati ya Bidii',
    loading: 'Inapakia data ya uzingatiaji...',
    loadError: 'Imeshindwa kupakia data ya uzingatiaji',
    loadErrorTitle: 'Imeshindwa Kupakia Data ya EUDR',
    exportError: 'Utoaji umeshindwa — jaribu tena',
    generating: 'Inatayarisha...',
    readyForEU: 'Mahitaji yote yametimizwa — shamba lako liko tayari kwa soko la EU',
    attentionDetail: 'Kamilisha vitu vilivyo chini ili kukidhi mahitaji ya Udhibiti wa Ukataji Miti wa EU (2023/1115)',
    treesRegistered: 'Miti Iliyosajiliwa',
    deforestationRisk: 'Hatari ya Ukataji Miti',
    idDocument: 'Hati ya Kitambulisho',
    none: 'Hakuna',
    article3a: 'EUDR Kifungu 3(a) — Bila ukataji miti',
    article3aDetail: 'Miti {total} yote ina NDVI ≥ 0.3 katika rekodi za Sentinel-2 zilizopo (EUDR cutoff: Des 2020)',
    article3aNoTrees: 'Sajili angalau mti mmoja ili kuanza ufuatiliaji',
    article3aFlagged: 'Mti {count} umeainishwa — pitia ukurasa wa ufuatiliaji wa NDVI',
    article3bIdentity: 'EUDR Kifungu 3(b) — Uhalali (Kitambulisho cha Mkulima)',
    article3bIdVerified: 'Kitambulisho cha serikali kimepakiwa na kuthibitishwa na msimamizi',
    article3bIdPending: 'Kitambulisho kimepakiwa, kinasubiri uthibitisho wa msimamizi',
    article3bIdMissing: 'Pakia kitambulisho cha serikali kwenye Wasifu',
    article3bLand: 'EUDR Kifungu 3(b) — Uhalali (Hati ya Ardhi)',
    article3bLandVerified: 'Hati ya ardhi imepakiwa na kuthibitishwa na msimamizi',
    article3bLandPending: 'Hati ya ardhi imepakiwa, inasubiri uthibitisho wa msimamizi',
    article3bLandMissing: 'Pakia hati ya ardhi kwenye Wasifu',
    article9Geo: 'EUDR Kifungu 9(1)(d) — Mahali pa kijiografia',
    article9GeoDetail: 'Miti {count} yenye kuratibu za GPS imesajiliwa',
    article9GeoNoTrees: 'Hakuna miti iliyosajiliwa — ongeza mti wako wa kwanza',
    article9Trace: 'EUDR Kifungu 9(1)(a) — Ufuatiliaji',
    article9TraceDetail: 'Data yote imerekodiwa kwenye rekodi isiyobadilika (SHA256 hash-chain)',
    view: 'Angalia',
    fix: 'Rekebisha',
    dueDiligenceStatement: 'Hati ya Bidii',
    downloadJSON: 'Pakua JSON',
    statementId: 'Kitambulisho cha Hati:',
  },
  ndvi: {
    title: 'Ufuatiliaji wa NDVI',
    loading: 'Inapakia data ya NDVI...',
    loadError: 'Imeshindwa kupakia data ya NDVI',
    loadErrorTitle: 'Imeshindwa Kupakia NDVI',
    noTrees: 'Hakuna Miti Bado',
    noTreesDetail: 'Sajili miti ili kuona data ya ufuatiliaji wa NDVI.',
    treesMonitored: 'Miti Inayofuatiliwa',
    highRisk: 'Hatari Kubwa',
    mediumRisk: 'Hatari ya Kati',
    lowRisk: 'Hatari Ndogo',
    flagged: 'Miti {count} imeainishwa kwa hatari ya ukataji miti. Pitia historia ya NDVI hapa chini.',
    ndviDataPoints: 'Sehemu za data za NDVI: {count} | Ya hivi punde: {value}',
    noNDVI: 'hakuna NDVI',
    na: 'H.',
    refresh: 'Onyesha upya',
  },
  qrLabels: {
    title: 'Leburu za Miti',
    loading: 'Inapakia leburu...',
    loadError: 'Imeshindwa kupakia leburu za miti',
    loadErrorTitle: 'Imeshindwa Kupakia Leburu',
    noTrees: 'Hakuna Miti',
    noTreesDetail: 'Sajili miti ili kuchapisha leburu za QR.',
    instructions: 'Maagizo ya Leburu:',
    instruction1: 'Chapisha kwenye karatasi ya stika (A4, leburu 2" × 1" zinapendekezwa)',
    instruction2: 'Kata leburu na ambatanisha kwenye miti kwa tagi ya alumini + waya',
    instruction3: 'QR code inaunganisha kwenye ukurasa wa mti wenye data kamili ya kaboni',
    instruction4: 'Leburu ni sugu kwa maji zikilaminishwa au kutumia karatasi ya polyester',
    printLabels: 'Chapisha Leburu {count}',
    co2: 'kg CO₂',
    printTitle: 'Leburu za Miti za AvoClima — {farmer}',
    treeLabels: 'Leburu {count} za miti',
  },
  crcf: {
    title: 'EU CRCF Ukulima wa Kaboni',
    baseline: 'Msingi wa Kaboni',
    additionality: 'Nyongeza',
    monitoring: 'Mpango wa Ufuatiliaji',
    tco2: 'Jumla ya tCO₂',
    tco2PerHa: 'tCO₂/ha',
    soc: 'SOC (tC/ha makadirio)',
    species: 'Aina',
    additional: 'Kaboni ya Ziada',
    notAdditional: 'Sio Ziada',
  },
  protected: {
    signInRequired: 'Inahitaji Kuingia',
    description: 'Unahitaji kuingia ili kufikia ukurasa huu.',
  },
  common: {
    loading: 'Inapakia...',
    save: 'Hifadhi Mabadiliko',
    saving: 'Inahifadhi...',
    cancel: 'Ghairi',
    confirm: 'Thibitisha',
    delete: 'Futa',
    back: 'Nyuma',
    next: 'Mbele',
    download: 'Pakua',
    print: 'Chapisha',
    refresh: 'Onyesha upya',
    offline: 'Haiko mtandaoni — data itasawazishwa kiotomatiki',
    pendingSync: 'Vitu {count} vinasubiri kulandanishwa',
    syncNow: 'Landanisha Sasa',
    retry: 'Jaribu tena',
  },
  home: {
    tagline: 'Ufuatiliaji wa Miti ya Kijiografia na Mazingira kwa Masoko ya Kaboni ya Misitu ya Kilimo',
    mission: 'Dhamira Yetu',
    missionText: 'AvoClima inawawezesha wakulima wadogo kwa data ya hesabu ya miti yenye uadilifu wa juu, ufuatiliaji wa setilaiti, na uidhinishaji wa mikopo ya kaboni — kufungua fedha za hali ya hewa kwa jamii za vijijini.',
    howItWorks: 'Jinsi Inavyofanya Kazi',
    step1Title: 'Sajili Shamba Lako',
    step1Desc: 'Unda wasifu wa shamba, pakia hati za kitambulisho na hati ya ardhi, na ramani mpaka wako kwa kutumia GPS.',
    step2Title: 'Ongeza Miti na Picha',
    step2Desc: 'Simama chini ya kila mti, pima DBH kwa kipimo cha kipenyo, nase eneo la GPS na picha za pembe 4. Injini yetu ya photogrammetry hukokotoa urefu na kifuniko kutoka kwa picha zako. Kaboni hukokotwa kutoka kwa kipimo chako cha DBH na aina ya mti uliyochagua.',
    step3Title: 'Ufuatiliaji wa Setilaiti',
    step3Desc: 'NDVI ya Sentinel-2 kila mwezi hufuatilia afya ya mimea na hugundua hatari ya ukataji miti, ikitoa uthibitishaji wa setilaiti unaoendelea wa uhai wa mti.',
    step4Title: 'Uthibitishaji wa Kaboni',
    step4Desc: 'Timiza mahitaji ya EUDR na CRCF. Tayarisha hati ya bidii na ripoti za mikopo ya kaboni.',
    carbonEngine: 'Injini ya Kaboni',
    aboveGroundBiomass: 'Uzito wa Mimea Juu ya Ardhi (kg)',
    carbonContent: 'Maudhui ya kaboni (IPCC 0.47 default)',
    co2Equivalent: 'Sawa ya CO₂ (×3.67)',
    monteCarloNote: 'Mahesabu ya kaboni hutumia uchambuzi wa kutokuwa na uhakika wa Monte Carlo (marudio 10,000) kwa mipaka ya uhakika ya 95%.',
  },
  dashboard: {
    loadError: 'Imeshindwa kupakia data ya ramani',
  },
  admin: {
    title: 'Dashibodi ya Msimamizi',
    accessDenied: 'Hakuna Idhini',
    privilegesRequired: 'Haki za msimamizi zinahitajika.',
    loading: 'Inapakia paneli ya msimamizi...',
    loadError: 'Imeshindwa kupakia data ya msimamizi',
    overview: 'Muhtasari',
    farmers: 'Wakulima',
    allTrees: 'Miti Yote',
    ndviStatus: 'Hali ya NDVI',
    charts: 'Chati',
    eudrExport: 'Utoaji wa EUDR',
    pendingCreditsTab: 'Mikopo Inayosubiri ({count})',
    totalFarmers: 'Jumla ya Wakulima',
    totalTrees: 'Jumla ya Miti',
    activeTrees: 'Miti Hai',
    co2Issued: 'CO2 Iliyotolewa (kg)',
    pending: 'Inasubiri',
    approvedCredits: 'Mikopo Iliyoidhinishwa',
    issuedCredits: 'Mikopo Iliyotolewa',
    monitoringTrends: 'Mwenendo wa Ufuatiliaji',
    monitoringDesc: 'Mwenendo wa kila mwezi, usambazaji wa aina na afya, uchanganuzi wa NDVI, na hali ya mkopo wa kaboni.',
    tableId: 'Kitambulisho',
    tableName: 'Jina',
    tableEmail: 'Barua Pepe',
    tableFarm: 'Shamba',
    tableTrees: 'Miti',
    tableVerified: 'Imethibitishwa',
    tableDocs: 'Nyaraka',
    tableActions: 'Vitendo',
    verified: 'Imethibitishwa',
    none: 'Hakuna',
    admin: 'Msimamizi',
    unverify: 'Ondoa Uthibitisho',
    verify: 'Thibitisha',
    verifyDocs: 'Thibitisha Nyaraka',
    delete: 'Futa',
    tableSpecies: 'Aina',
    tableDbh: 'DBH',
    tableCo2: 'CO2 (kg)',
    tableStatus: 'Hali',
    ndviDesc: 'Hali ya mimea ya NDVI kwa miti yote hai. Nyekundu = hatari kubwa ya ukataji miti.',
    tableTreeId: 'Kitambulisho cha Mti',
    ndvi: 'NDVI',
    date: 'Tarehe',
    risk: 'Hatari',
    noNDVIData: 'Hakuna data ya NDVI inayopatikana.',
    low: 'NDOGO',
    medium: 'KATI',
    high: 'KUBWA',
    riskInfo: 'Hatari kubwa: NDVI < 0.1 | Kati: 0.1-0.3 | Ndogo: > 0.3',
    eudrTitle: 'Utoaji wa Hati ya Bidii ya EUDR',
    eudrDesc: 'Tayarisha hati za bidii za EUDR kwa wakulima. Hii inatoa kuratibu za GPS, mfululizo wa NDVI, mpaka wa shamba, hatari ya ukataji miti, na hali ya uthibitishaji wa mkulima katika muundo unaoendana na mahitaji ya Mfumo wa Taarifa wa EU (Kanuni 2023/1115).',
    ndviAvg: 'Wastani wa NDVI',
    generating: 'Inatayarisha...',
    dueDiligence: 'Hati ya Bidii',
    eudrStatement: 'Hati ya EUDR',
    downloadJSON: 'Pakua JSON',
    noPendingCredits: 'Hakuna mikopo inayosubiri kupitiwa.',
    approve: 'Idhinisha',
    approveAndIssue: 'Idhinisha na Toa',
    howCreditsWorkTitle: 'Jinsi Utoaji wa Mikopo Unavyofanya Kazi',
    creditStep1: 'Vipimo vya miti vinaunda maingizo ya leja ya kaboni inayosubiri',
    creditStep2: 'Msimamizi anaidhinisha hesabu (anathibitisha DBH, aina, uadilifu wa data)',
    creditStep3: 'Msimamizi anatoa mkopo, akitoa kitambulisho cha mkopo cha AC-XXXXXXX kinachoweza kuthibitishwa',
    creditStep4: 'Mtu yeyote anaweza kuthibitisha mkopo kwenye /api/v1/carbon/verify kwa kutumia kitambulisho cha mkopo',
    deleteFarmerTitle: 'Futa Mkulima',
    deleteFarmerMsg: 'Futa mkulima "{name}" na miti yao YOTE, picha, na data? Hili haliwezi kutenguliwa.',
    deleteTreeTitle: 'Futa Mti',
    deleteTreeMsg: 'Futa mti #{id} na data yake yote?',
    creditIssued: 'Mkopo Umetolewa!',
    creditId: 'Kitambulisho cha Mkopo: {id}',
    exportFailed: 'Utoaji Umeshinduwa',
    exportFailedMsg: 'Haikuweza kuzalisha hati ya EUDR',
  },
  profile: {
    title: 'Wasifu Wangu',
    email: 'Barua Pepe',
    name: 'Jina',
    emailCannotBeChanged: 'Barua pepe haiwezi kubadilishwa',
    namePlaceholder: 'Jina lako',
    farmNamePlaceholder: 'Jina la shamba lako',
    locationPlaceholder: 'mf. Kiambu, Kenya',
    hectaresPlaceholder: '0.0',
    phonePlaceholder: '+254...',
    eudrDocs: 'Nyaraka za Uzingatiaji wa EUDR',
    notUploaded: 'Haijapakiwa',
    uploadedVerified: '(Imethibitishwa)',
    uploadedPending: '(Inasubiri uthibitisho)',
    saveSuccess: 'Wasifu umesasishwa kwa ufanisi',
    saveFailed: 'Imeshindwa kuhifadhi wasifu',
    idUploaded: 'Kitambulisho kimepakiwa',
    landDeedUploaded: 'Hati ya ardhi imepakiwa',
    uploadFailed: 'Upakiaji umeshindwa kwa {type}',
    docLoadError: 'Haikuweza kupakia maelezo ya hati',
    uploading: 'Inapakia...',
  },
  auth: {
    login: 'Ingia',
    loggingIn: 'Inaingiza...',
    email: 'Barua Pepe',
    emailPlaceholder: 'your@email.com',
    password: 'Nywila',
    passwordPlaceholder: 'Weka nywila yako',
    forgotPassword: 'Umesahau nywila?',
    noAccount: 'Huna akaunti?',
    registerHere: 'Jisajili hapa',
    loginFailed: 'Uingiaji umeshindwa',
    register: 'Unda Akaunti Yako',
    registerDesc: 'Sajili shamba lako ili kuanza kufuatilia miti na hifadhi ya kaboni.',
    fullName: 'Jina Kamili',
    fullNamePlaceholder: 'mf. Jane Wanjiku',
    passwordPlaceholderRegister: 'Angalau herufi 8',
    farmNamePlaceholder: 'mf. Shamba la Jua',
    locationPlaceholder: "mf. Murang'a, Kenya",
    hectaresPlaceholder: 'mf. 3.8',
    creating: 'Inaunda akaunti...',
    create: 'Unda Akaunti',
    alreadyAccount: 'Tayari una akaunti?',
    loginLink: 'Ingia',
    allFieldsRequired: 'Sehemu zote zinahitajika.',
    passwordMinLength: 'Nywila lazima iwe angalau herufi 8.',
    registrationFailed: 'Usajili umeshindwa',
    welcome: 'Karibu, {name}! Angalia barua pepe yako (au koni ya seva) kuthibitisha akaunti yako.',
    resetPassword: 'Weka Upya Nywila',
    sendResetLink: 'Tuma Kiungo cha Kuweka Upya',
    sending: 'Inatuma...',
    resetSent: 'Ikiwa barua pepe hiyo imesajiliwa, kiungo cha kuweka upya kimetumwa.',
    resetDevNote: '(Katika hali ya maendeleo, angalia koni ya seva kwa tokeni ya kuweka upya.)',
    backToLogin: 'Rudi kwenye Ingia',
    enterEmail: 'Weka barua pepe yako na tutakutumia kiungo cha kuweka upya.',
    resetFail: 'Ombi la kuweka upya lilishindwa',
    setNewPassword: 'Weka Nywila Mpya',
    newPassword: 'Nywila Mpya',
    newPasswordPlaceholder: 'Angalau herufi 8',
    resetting: 'Inaweka upya...',
    resetPwdBtn: 'Weka Upya Nywila',
    invalidResetLink: 'Kiungo Batili cha Kuweka Upya',
    noResetToken: 'Hakuna tokeni ya kuweka upya iliyopatikana. Omba kiungo kipya cha kuweka upya.',
    requestReset: 'Omba Kuweka Upya',
    resetSuccess: 'Nywila imewekwa upya kwa ufanisi!',
    resetFailed: 'Kuweka upya kumeshindwa. Kiungo kinaweza kuwa kimeisha au kutumika tayari.',
  },
  field: {
    title: 'Uthibitishaji wa Shamba',
    pending: '{count} inasubiri',
    syncing: 'Inalandanisha...',
    syncAll: 'Landanisha Zote ({count})',
    measurementsSynced: 'Vipimo vimelandanishwa na seva',
    selectTree: 'Chagua Mti',
    noTreesFound: 'Hakuna miti iliyopatikana',
    treeNum: 'Vipimo vya Mti #{id}',
    selectATree: 'Chagua mti',
    dbh: 'DBH (cm)',
    gpsCoords: 'Kuratibu za GPS',
    latitude: 'Latitudo',
    longitude: 'Longitudo',
    fixingGps: 'Inatafuta GPS...',
    captureGps: 'Nasa GPS',
    recaptureGps: 'Nasa GPS Tena',
    fieldNotes: 'Maelezo ya Shamba',
    notesPlaceholder: 'Maelezo kuhusu hali ya mti, wadudu, nk.',
    clear: 'Futa',
    saveAll: 'Hifadhi Zote',
    offlineSafe: 'Ukusanyaji wa Data Salama Nje ya Mtandao',
    offlineNote1: 'Vipimo vyote vimehifadhiwa ndani ya kivinjari chako na vinaishi hata ukionyesha upya ukurasa',
    offlineNote2: 'Ukibofya Landanisha Zote, data inatumwa kwa seva mti kwa mti',
    offlineNote3: 'Ikiwa seva haipatikani, data inabaki kwenye hifadhi ya ndani hadi jaribio linalofuata la kulandanisha',
    offlineNote4: 'Tumia kitufe cha Utoaji CSV kupakua vipimo vyote kama lahajedwali kwa ajili ya chelezo',
    offlineNote5: 'Ukamataji wa GPS hutumia API ya eneo la kivinjari (wezesha usahihi wa juu kwa usahihi wa ±3 m)',
    csv: 'CSV',
    treeLoadError: 'Haikuweza kupakia miti kutoka kwa seva — angalia muunganisho',
  },
  trends: {
    monthlyTrees: 'Usajili wa Miti Kila Mwezi',
    monthlyCarbon: 'CO₂ Kila Mwezi (kg)',
    monthlyFarmers: 'Usajili wa Wakulima Kila Mwezi',
    speciesDistribution: 'Usambazaji wa Aina',
    healthStatus: 'Hali ya Afya',
    ndviDistribution: 'Usambazaji wa NDVI',
    creditStatus: 'Hali ya Mikopo',
    noData: 'Hakuna data ya mwenendo',
    max: 'upeo',
  },
  crcf: {
    title: 'EU CRCF Ukulima wa Kaboni',
    baseline: 'Msingi wa Kaboni',
    additionality: 'Nyongeza',
    monitoring: 'Mpango wa Ufuatiliaji',
    tco2: 'Jumla ya tCO₂',
    tco2PerHa: 'tCO₂/ha',
    soc: 'SOC (tC/ha makadirio)',
    species: 'Aina',
    additional: 'Kaboni ya Ziada',
    notAdditional: 'Sio Ziada',
    loading: 'Inapakia data ya ukulima wa kaboni...',
    loadError: 'Imeshindwa kupakia data ya ukulima wa kaboni',
    noTreesYet: 'Hakuna Miti Bado',
    noTreesDesc: 'Sajili miti ili kuanzisha msingi wako wa kaboni.',
    retry: 'Jaribu tena',
    regulation: 'Kanuni (EU) 2024/3012 — Mfumo wa Uthibitishaji wa Uondoaji wa Kaboni',
    whatThisMeans: 'Maana yake:',
    crcfBaselineDesc: 'Huu ndio msingi wako wa CRCF. Faida zote za kaboni za baadaye hupimwa dhidi ya hatua hii ya kuanzia. SOC hutumia viwango vya msingi vya IPCC Tier 1 kulingana na eneo la hali ya hewa na aina ya udongo — uchunguzi wa kitaalamu wa udongo unaweza kuboresha thamani hii. Msingi lazima uanzishwe kabla ya shughuli za ukulima wa kaboni kuanza.',
    carbonAdditionality: 'Nyongeza ya Kaboni',
    bauReference: 'Kumbukumbu ya BAU ({reference})',
    currentTreeCarbon: 'Kaboni ya sasa ya miti',
    estimatedSocGain: 'Makadirio ya faida ya SOC (miaka 10)',
    additionalCarbon: 'Kaboni ya ziada',
    barriersToAddress: 'Vikwazo vya kushughulikia',
    monitoringPlan: 'Mpango wa Ufuatiliaji',
    parameter: 'Kigezo',
    method: 'Mbinu',
    frequency: 'Mara kwa mara',
    qualityAssurance: 'Uhakikisho wa Ubora',
  },
  about: {
    title: 'Kuhusu AvoClima',
    problem: 'Tatizo',
    problemText: 'Ukulima wa misitu unapanuka kwa kasi Afrika Mashariki — mashamba ya parachichi, maembe, makadamia, grevillea, eucalyptus, na kahawa yanahifadhi kaboni nyingi. Hata hivyo, wakulima, vyama vya ushirika, na wasafirishaji wanakosa zana za vitendo za kupima, kufuatilia, na kuthibitisha hifadhi za kaboni za miti. Majukwaa yaliyopo ya dMRV ni ghali, magumu, na hayajakusudiwa kwa ukulima mdogo wa misitu.',
    solution: 'Suluhisho Letu',
    solutionText: 'AvoClima ni jukwaa la hesabu ya miti ya kijiografia na ufuatiliaji lililojengwa kwa ukulima mdogo wa misitu. Inachanganya:',
    feature1Title: 'Ukusanyaji wa data kwa MSH-15',
    feature1Desc: 'Kifaa chetu cha vipimo hukusanya data ya miti kwa GPS ya usahihi wa sentimita (RTK)',
    feature2Title: 'Injini ya kaboni ya aina maalum',
    feature2Desc: 'Aina 16 za miti na mipaka ya kutokuwa na uhakika ya Monte Carlo',
    feature3Title: 'Photogrammetry ya kamera nyingi',
    feature3Desc: 'inapima urefu, DBH na taji kutoka kamera 12 zilizosawazishwa',
    feature4Title: 'Rekodi isiyobadilika',
    feature4Desc: 'kila hatua imerekodiwa kwa uthibitishaji wa vipimo',
    feature5Title: 'Uthibitishaji wa setilaiti',
    feature5Desc: 'Sentinel-2 NDVI inathibitisha mimea katika kila eneo la mti',
    techStack: 'Msururu wa Teknolojia',
    verifiability: 'Uthibitishaji',
    verifiabilityText: 'Kila kipimo cha mti huunda ingizo la leja ya kaboni yenye muda na kutokuwa na uhakika uliohesabiwa. GPS ya EXIF ya picha inathibitishwa dhidi ya kuratibu zilizosajiliwa za mti kwa uthabiti wa eneo. Alama za mimea kutoka kwa uchambuzi wa saizi huthibitisha uwepo wa mti. Rekodi isiyobadilika inarekodi kila hatua. Sentinel-2 NDVI hutoa uthibitishaji huru wa setilaiti.',
  },
};

const translations = { en, sw };

let currentLocale = 'en';

export function setLocale(locale) {
  if (translations[locale]) {
    currentLocale = locale;
    localStorage.setItem('avoclima-locale', locale);
  }
}

export function getLocale() {
  return currentLocale;
}

export function initLocale() {
  const saved = localStorage.getItem('avoclima-locale');
  if (saved && translations[saved]) {
    currentLocale = saved;
  }
  return currentLocale;
}

export function t(key, params = {}) {
  const keys = key.split('.');
  let val = translations[currentLocale];
  for (const k of keys) {
    if (!val || !val[k]) {
      // Fallback to English
      let fallback = translations.en;
      for (const fk of keys) {
        fallback = fallback?.[fk];
      }
      val = fallback;
      break;
    }
    val = val[k];
  }
  if (typeof val === 'string') {
    return val.replace(/\{(\w+)\}/g, (_, p) => params[p] ?? `{${p}}`);
  }
  return key;
}
